import { Inject, Injectable } from '@angular/core';
import { DwOrmDataServiceToken, DwOrmDataService, DwQueryHelper, DwMenuCategory, sortArrayByField, DwEventService, DwCacheService, DwCacheInvalidationTopic, DwMetaDataService, DwMetaDataServiceToken } from '@devwareapps/devware-cap';
import { EmployeeReviewCategoryEntity, PerformanceManagementMetaDataService, EmployeeReviewAnswerValueEntity, EmployeeReviewAnswerEntity, EmployeeReviewSummaryEntity, EthosUserEntity, EmployeeReviewQuestionSetupEntity, EmployeeReviewQuestionEntity, RoEthosUserEntity, EmployeeReviewEntity, PerformanceManagementMetaDataItemNames } from '../meta-data/performancemanagement-meta-data.service';
import { Observable, combineLatest, of, forkJoin } from 'rxjs';
import { EmployeeReviewFlatAnswerValues, EmployeeReviewQuestionData } from '../models/employee-review-question-data.model';
import { map, mergeMap, concatAll } from 'rxjs/operators';
import { WachterUserExt } from '../../../shared/models/wachter-user.model';
import { EmployeeReviewQuestionSetupLevel } from '../meta-data/constants/employee-review-question-setup-level.enum';
import { pipeFromArray } from 'rxjs/internal/util/pipe';
import { EmsUserExtendedEntity, AppMetaDataItemNames, AppMetaDataService } from '../../../meta-data/app-meta-data.service';
import { EmployeeReviewSummaryStatus } from '../meta-data/constants/employee-review-summary-status.enum';

@Injectable({ providedIn: 'root' })
export class PerformanceManagementRepositoryService {

    constructor(@Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
        @Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService,
        private dwEventsService: DwEventService,
        private dwCacheService: DwCacheService,
    ) {
    }

    getEmployeeReviews(employeeReviewSummaryId: number, loadDetails: boolean = false): Observable<EmployeeReviewEntity[]> {
        const md = new PerformanceManagementMetaDataService();
        const qh = new DwQueryHelper();

        const query = md.EmployeeReview.CreateQuery();

        query.AddFilterAnd(qh.filters.Equal(md.EmployeeReview.EmployeeReviewSummaryId, employeeReviewSummaryId));

        if (loadDetails) {
            query.AddPrefetchPath(md.EmployeeReview.Relations.EmployeeReviewAnswer);
        }

        query.FieldSettings.LoadAllLookupDisplayFields = true;

        return this.dwOrmDataService.executeQuery(query);
    }


    getEmployeeReviewAnswers(employeeReviewId: number): Observable<EmployeeReviewAnswerEntity[]> {
        const md = new PerformanceManagementMetaDataService();
        const qh = new DwQueryHelper();


        const query = md.EmployeeReviewAnswer.CreateQuery();

        query.AddFilterAnd(qh.filters.Equal(md.EmployeeReviewAnswer.EmployeeReviewId, employeeReviewId));

        query.FieldSettings.LoadAllLookupDisplayFields = true;

        return this.dwOrmDataService.executeQuery(query);
    }

    getEmployeeReviewQuestions(): Observable<EmployeeReviewQuestionData> {
        const cacheInvalidationTopics: string[] = [];

        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(PerformanceManagementMetaDataItemNames.EmployeeReviewCategory));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(PerformanceManagementMetaDataItemNames.EmployeeReviewQuestion));
        
        return this.dwCacheService.get('employeeReviewQuestions', this.loadEmployeeReviewQuestions(), 60, cacheInvalidationTopics);
    }

    loadEmployeeReviewQuestions(): Observable<EmployeeReviewQuestionData> {
        const obCategories = this.loadReviewCategories();
        const obAnswerValues = this.loadReviewAnswerValues();

        return combineLatest([obCategories, obAnswerValues])
            .pipe(map(([categories, answerValues]) => {

                const data: EmployeeReviewQuestionData = {
                    categories: categories,
                    answerValues: answerValues,

                    flatAnswerValues: this.flattenAnswerValues(answerValues)
                };

                return data;
            }));
    }

    canAccessEmsExtenededUser() : Observable<boolean> {
        return this.dwMetaDataService.getUserMetaDataItemSecurity(AppMetaDataItemNames.EmsUserExtended)
            .pipe(map(mdItemSecurity => {
                return mdItemSecurity.CanRead;
            }));
    }

    loadEMSExtendedUser(ethosUserId: number): Observable<EmsUserExtendedEntity> {
        const md = new AppMetaDataService();
        const qh = new DwQueryHelper();

     
        const query = md.EmsUserExtended.CreateQuery();

        query.AddFilterAnd(qh.filters.Equal(md.EmsUserExtended.EmployeeEthosUserId, ethosUserId));

        // We just need the PK
        query.FieldSettings.IncludeRootItemFields = false;

        return this.dwOrmDataService.executeQuerySingle(query);
    }

    flattenAnswerValues(answerValues: EmployeeReviewAnswerValueEntity[]): EmployeeReviewFlatAnswerValues {
        const flatAnswerValues: EmployeeReviewFlatAnswerValues = {};

        for (const answerValue of answerValues) {
            flatAnswerValues[answerValue.EmployeeReviewAnswerValueId] = answerValue;
        }

        return flatAnswerValues;
    }

    createEmployeeReviewQuestionSetup(data: EmployeeReviewQuestionSetupEntity) {

        const md = new PerformanceManagementMetaDataService();

        var query = md.EmployeeReviewQuestionSetup.CreateQuery();

        query.FieldSettings.LoadAllLookupDisplayFields = true;

        return this.dwOrmDataService.saveEntity(data, false, query);
    }

    getEthosUserInfo(trustUserId: number): Observable<RoEthosUserEntity> {
        return this.getEthosUsersInfo(trustUserId)
            .pipe(map(trustUsers => {

                return trustUsers[0];

            }));
    }

    getEthosUsersInfo(...trustUserIds: number[]): Observable<RoEthosUserEntity[]> {
        if (trustUserIds.length == 0) {
            return of([]);
        }

        const md = new PerformanceManagementMetaDataService();
        const qh = new DwQueryHelper();

        const query = md.RoEthosUser.CreateQuery();

        query.AddFields([md.RoEthosUser.UserId, md.RoEthosUser.SupervisorUserId, md.RoEthosUser.WachterDepartmentId, md.RoEthosUser.WachterTeamId, md.RoEthosUser.WachterDivisionId, md.RoEthosUser.NameLastFirstPreferedType, md.RoEthosUser.FirstName, md.RoEthosUser.LastName, md.RoEthosUser.PreferredName, md.RoEthosUser.NamePreferedLast]);

        query.AddFilterAnd(qh.filters.InList(md.RoEthosUser.UserId, trustUserIds));

        return this.dwOrmDataService.executeQuery(query);
    }

    /**
     * Gets all question setups to show on the aval page
     * @param level - Global, Department or Team
     * @param reviewYear 
     * @param wachterDepartmentId - User's department Id
     * @returns 
     */
    getQuestionSetupByLevel(level: EmployeeReviewQuestionSetupLevel, reviewYear: number, wachterDepartmentId?: number): Observable<EmployeeReviewQuestionSetupEntity[]> {
        const md = new PerformanceManagementMetaDataService();
        const qh = new DwQueryHelper();

        // No need to load anything if we're gloabl
        if (level == EmployeeReviewQuestionSetupLevel.global) {
            return of([]);
        }

        const query = md.EmployeeReviewQuestionSetup.CreateQuery();

        query.FieldSettings.LoadAllLookupDisplayFields = true;

        query.AddFilterAnd(qh.filters.Equal(md.EmployeeReviewQuestionSetup.ReviewYear, reviewYear));

        const filter = qh.filters.Group();

        query.AddFilterAnd(filter);

        filter.AddFilterOr(qh.filters.Equal(md.EmployeeReviewQuestionSetup.EmployeeReviewQuestionSetupLevelId, EmployeeReviewQuestionSetupLevel.global));

        if (level == EmployeeReviewQuestionSetupLevel.team && wachterDepartmentId) {
            const departmentFilter = qh.filters.Group();

            departmentFilter.AddFilterAnd(qh.filters.Equal(md.EmployeeReviewQuestionSetup.EmployeeReviewQuestionSetupLevelId, EmployeeReviewQuestionSetupLevel.department));
            departmentFilter.AddFilterAnd(qh.filters.Equal(md.EmployeeReviewQuestionSetup.WachterDepartmentId, wachterDepartmentId));

            filter.AddFilterOr(departmentFilter);
        }

        const subQuery = md.EmployeeReviewQuestionSetupDetail.CreateQuery();

        subQuery.AddPrefetchPath(md.EmployeeReviewQuestionSetupDetail.Relations.EmployeeReviewQuestion);

        query.AddPrefetchPath(md.EmployeeReviewQuestionSetup.Relations.EmployeeReviewQuestionSetupDetail, subQuery);


        query.OrderBy(md.EmployeeReviewQuestionSetup.EmployeeReviewQuestionSetupLevelId);

        return this.dwOrmDataService.executeQuery(query);
    }


    /**
     * Gets all question setups to show on the aval page
     * @param reviewYear 
     * @param trustUserId - User's department Id
     * @returns 
     */
    getQuestionSetupForEval(reviewYear: number, trustUserId: number): Observable<EmployeeReviewQuestionSetupEntity[]> {
        return this.getEthosUsersInfo(trustUserId)
            .pipe(mergeMap(trustUsers => {
                if (!trustUsers || trustUsers.length == 0) {
                    return of([]);
                }
                const trustUser = trustUsers[0];

                return this.getQuestionSetupForEvalWithTrustUser(reviewYear, trustUser);
            }));
    }

    /**
    * Gets all question setups to show on the aval page
    * @param reviewYear 
    * @param trustUserId - User's department Id
    * @returns 
    */
    getQuestionSetupForEvalWithTrustUser(reviewYear: number, trustUser: RoEthosUserEntity): Observable<EmployeeReviewQuestionSetupEntity[]> {
        return this.getQuestionSetupForEvalByDepartmentTeam(reviewYear, trustUser.WachterDepartmentId, trustUser.WachterTeamId);
    }


    /**
     * When a Department question setup changes the Hide Revenue/Bidding questions, this updates the team to clear those values (otherwise they will show on the Review Questions Setup card incorrectly)
     * @returns Saved teams setup questions
     */
    updateTeamBiddingAndRevenueQuestions(WachterDepartmentId: number, reviewYear: number, HideRevenueQuestions: boolean, HideBiddingQuestions: boolean): Observable<EmployeeReviewQuestionSetupEntity[]> {
        // First, we need to load the team records for this department

        return this.getTeamQuestions(WachterDepartmentId, reviewYear)
            .pipe(mergeMap(teams => {

                const teamsToUpdate: EmployeeReviewQuestionSetupEntity[] = [];

                for (const team of teams) {
                    let hasChanges = false;
                    if (HideRevenueQuestions && team.HideRevenueQuestions) {
                        team.HideRevenueQuestions = false;
                        hasChanges = true;
                    }

                    if (HideBiddingQuestions && team.HideBiddingQuestions) {
                        team.HideBiddingQuestions = false;
                        hasChanges = true;
                    }

                    if (hasChanges) {
                        teamsToUpdate.push(team);
                    }
                }

                // If not changes, then no need to change
                if (teamsToUpdate.length == 0) {
                    return of(null);
                }

                // Save the teams
                return this.dwOrmDataService.saveEntityCollection(teamsToUpdate)
                    .pipe(map(savedTeamSetups => {
                        // Publish an event since this happens after the parent save
                        this.dwEventsService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(PerformanceManagementMetaDataItemNames.EmployeeReviewQuestionSetup), {});

                        return savedTeamSetups;
                    }))
            }));
    }

    private getTeamQuestions(wachterDepartmentId: number, reviewYear: number): Observable<EmployeeReviewQuestionSetupEntity[]> {
        const md = new PerformanceManagementMetaDataService();
        const qh = new DwQueryHelper();

        const query = md.EmployeeReviewQuestionSetup.CreateQuery();

        query.AddFilterAnd(qh.filters.Equal(md.EmployeeReviewQuestionSetup.WachterDepartmentId, wachterDepartmentId));
        query.AddFilterAnd(qh.filters.Equal(md.EmployeeReviewQuestionSetup.EmployeeReviewQuestionSetupLevelId, EmployeeReviewQuestionSetupLevel.team));
        query.AddFilterAnd(qh.filters.Equal(md.EmployeeReviewQuestionSetup.ReviewYear, reviewYear));

        return this.dwOrmDataService.executeQuery(query);
    }


    private getQuestionSetupForEvalByDepartmentTeam(reviewYear: number, wachterDepartmentId?: number, wachterTeamId?: number): Observable<EmployeeReviewQuestionSetupEntity[]> {
        const md = new PerformanceManagementMetaDataService();
        const qh = new DwQueryHelper();

        const query = md.EmployeeReviewQuestionSetup.CreateQuery();

        query.FieldSettings.LoadAllLookupDisplayFields = true;

        query.AddFilterAnd(qh.filters.Equal(md.EmployeeReviewQuestionSetup.ReviewYear, reviewYear));

        const filter = qh.filters.Group();

        query.AddFilterAnd(filter);

        filter.AddFilterOr(qh.filters.Equal(md.EmployeeReviewQuestionSetup.EmployeeReviewQuestionSetupLevelId, EmployeeReviewQuestionSetupLevel.global));

        if (wachterDepartmentId) {
            const departmentFilter = qh.filters.Group();

            departmentFilter.AddFilterAnd(qh.filters.Equal(md.EmployeeReviewQuestionSetup.EmployeeReviewQuestionSetupLevelId, EmployeeReviewQuestionSetupLevel.department));
            departmentFilter.AddFilterAnd(qh.filters.Equal(md.EmployeeReviewQuestionSetup.WachterDepartmentId, wachterDepartmentId));

            filter.AddFilterOr(departmentFilter);
        }

        if (wachterTeamId) {
            const teamFilter = qh.filters.Group();

            teamFilter.AddFilterAnd(qh.filters.Equal(md.EmployeeReviewQuestionSetup.EmployeeReviewQuestionSetupLevelId, EmployeeReviewQuestionSetupLevel.team));
            teamFilter.AddFilterAnd(qh.filters.Equal(md.EmployeeReviewQuestionSetup.WachterTeamId, wachterTeamId));

            filter.AddFilterOr(teamFilter);
        }

        const subQuery = md.EmployeeReviewQuestionSetupDetail.CreateQuery();

        subQuery.AddPrefetchPath(md.EmployeeReviewQuestionSetupDetail.Relations.EmployeeReviewQuestion);

        query.AddPrefetchPath(md.EmployeeReviewQuestionSetup.Relations.EmployeeReviewQuestionSetupDetail, subQuery);

        query.OrderBy(md.EmployeeReviewQuestionSetup.EmployeeReviewQuestionSetupLevelId);

        return this.dwOrmDataService.executeQuery(query)
            .pipe(map((questionSetup: EmployeeReviewQuestionSetupEntity[]) => {
                return questionSetup;
            }));
    }

    getQuestionSetupForList(reviewYear: number, levelToLoad: EmployeeReviewQuestionSetupLevel, wachterDepartmentId?: number, wachterTeamId?: number): Observable<EmployeeReviewQuestionSetupEntity[]> {
        const md = new PerformanceManagementMetaDataService();
        const qh = new DwQueryHelper();

        const query = md.EmployeeReviewQuestionSetup.CreateQuery();

        query.FieldSettings.LoadAllLookupDisplayFields = true;

        query.AddFilterAnd(qh.filters.Equal(md.EmployeeReviewQuestionSetup.ReviewYear, reviewYear));

        const filter = qh.filters.Group();

        query.AddFilterAnd(filter);

        if (levelToLoad == EmployeeReviewQuestionSetupLevel.global) {
            filter.AddFilterOr(qh.filters.Equal(md.EmployeeReviewQuestionSetup.EmployeeReviewQuestionSetupLevelId, EmployeeReviewQuestionSetupLevel.global));
        }

        if (wachterDepartmentId && levelToLoad == EmployeeReviewQuestionSetupLevel.department) {
            const departmentFilter = qh.filters.Group();

            departmentFilter.AddFilterAnd(qh.filters.Equal(md.EmployeeReviewQuestionSetup.EmployeeReviewQuestionSetupLevelId, EmployeeReviewQuestionSetupLevel.department));
            departmentFilter.AddFilterAnd(qh.filters.Equal(md.EmployeeReviewQuestionSetup.WachterDepartmentId, wachterDepartmentId));

            filter.AddFilterOr(departmentFilter);
        }

        if (wachterTeamId && levelToLoad == EmployeeReviewQuestionSetupLevel.team) {
            const teamFilter = qh.filters.Group();

            teamFilter.AddFilterAnd(qh.filters.Equal(md.EmployeeReviewQuestionSetup.EmployeeReviewQuestionSetupLevelId, EmployeeReviewQuestionSetupLevel.team));
            teamFilter.AddFilterAnd(qh.filters.Equal(md.EmployeeReviewQuestionSetup.WachterTeamId, wachterTeamId));

            filter.AddFilterOr(teamFilter);
        }

        query.AddPrefetchPath(md.EmployeeReviewQuestionSetup.Relations.EmployeeReviewQuestionSetupDetail);
        query.OrderBy(md.EmployeeReviewQuestionSetup.EmployeeReviewQuestionSetupLevelId);

        return this.dwOrmDataService.executeQuery(query);
    }


    getEmployeeReviewSummaries(userIds: number[], reviewYear: number): Observable<EmployeeReviewSummaryEntity[]> {
        const md = new PerformanceManagementMetaDataService();
        const qh = new DwQueryHelper();

        if (userIds.length == 0) {
            return of ([]);
        }

        const query = md.EmployeeReviewSummary.CreateQuery()
            .AddFilterAnd(qh.filters.InList(md.EmployeeReviewSummary.EmployeeEthosUserId, userIds))
            .AddFilterAnd(qh.filters.Equal(md.EmployeeReviewSummary.ReviewYear, reviewYear))
            .AddFilterAnd(qh.filters.NotEqual(md.EmployeeReviewSummary.EmployeeReviewSummaryStatusId, EmployeeReviewSummaryStatus.deleted));

        query.FieldSettings.LoadAllLookupDisplayFields = true;

        query.AddPrefetchPath(md.EmployeeReviewSummary.Relations.EmployeeReview);

        return this.dwOrmDataService.executeQuery(query);
    }

    saveEmployeeReviewSummaries(reviewSummaries: EmployeeReviewSummaryEntity[]): Observable<EmployeeReviewSummaryEntity[]> {
        return this.dwOrmDataService.saveEntityCollection(reviewSummaries, true);
    }
    saveEmployeeReviews(reviews: EmployeeReviewEntity[]): Observable<EmployeeReviewEntity[]> 
    {
        const saves = reviews.map<Observable<EmployeeReviewEntity>>(r => this.dwOrmDataService.saveEntity(r, false));
        /* Combine into single observable and pipe into join processor */
        return forkJoin(saves);
    }
    saveEachEmployeeReviewSummaries(reviews: EmployeeReviewSummaryEntity[]): Observable<EmployeeReviewSummaryEntity[]> 
    {
        const saves = reviews.map<Observable<EmployeeReviewSummaryEntity>>(r => this.dwOrmDataService.saveEntity( r, true));
        /* Combine into single observable and pipe into join processor */
        return forkJoin(saves);
    }
    private loadReviewCategories(): Observable<EmployeeReviewCategoryEntity[]> {
        const md = new PerformanceManagementMetaDataService();
        const qh = new DwQueryHelper();

        const query = md.EmployeeReviewCategory.CreateQuery();

        query.FieldSettings.LoadAllLookupDisplayFields = true;

        const questionQuery = md.EmployeeReviewQuestion.CreateQuery();
        questionQuery.FieldSettings.LoadAllLookupDisplayFields = true;

        // Get the skill so we can identify hard/soft skills
        questionQuery.AddPrefetchPath(md.EmployeeReviewQuestion.Relations.EmployeeReviewQuestionSkill);

        query.AddPrefetchPath(md.EmployeeReviewCategory.Relations.EmployeeReviewQuestion, questionQuery);

        query.AddPrefetchPath(md.EmployeeReviewCategory.Relations.WachterContainerStyle);

        return this.dwOrmDataService.executeQuery(query);
    }

    private loadReviewAnswerValues(): Observable<EmployeeReviewAnswerValueEntity[]> {
        const md = new PerformanceManagementMetaDataService();
        const qh = new DwQueryHelper();

        const query = md.EmployeeReviewAnswerValue.CreateQuery();

        query.FieldSettings.LoadAllLookupDisplayFields = true;

        return this.dwOrmDataService.executeQuery(query);
    }



}